import { Alert } from '@/components/ui/alert';
import { Typo } from '@/components/ui/typography';
import { UserCartItem } from '@/hooks/use-user-global-cart';
import { AlertCircle } from 'lucide-react';
import { useMemo } from 'react';
import { CartServiceGroup } from './cart-service-group';

interface CartServiceSelectorProps {
  cart: UserCartItem[];
  selectedItems: Set<string>;
  onItemSelectionChange: (itemId: string, selected: boolean) => void;
  onBatchSelectionChange: (itemIds: string[], selected: boolean) => void;
  onItemRemove: (itemId: string) => Promise<void>;
  onUpdateQuantity: (rowId: string, quantity: number) => void;
  onContinueToCheckout: () => void;
  isLoading: boolean;
  isFetching: boolean;
}

export function CartServiceSelector({
  cart,
  selectedItems,
  onItemSelectionChange,
  onBatchSelectionChange,
  onItemRemove,
  onContinueToCheckout,
  onUpdateQuantity,
  isLoading,
  isFetching,
}: CartServiceSelectorProps) {
  // Group cart items by site and date
  const groupedCartItems = useMemo(() => {
    const groups: Record<
      string,
      {
        siteName: string;
        siteCode: string;
        date: string;
        items: UserCartItem[];
      }
    > = {};

    cart.forEach((item) => {
      const siteCode = item.options?.site_mapping_code || 'unknown';
      const siteName = item.options?.site_name || 'Địa điểm không xác định';
      const date = item.options?.date || '';
      const key = `${siteCode}_${date}`;

      if (!groups[key]) {
        groups[key] = {
          siteName,
          siteCode,
          date,
          items: [],
        };
      }
      groups[key].items.push(item);
    });

    return Object.values(groups);
  }, [cart]);

  // Enhanced item selection handler
  const handleItemSelectionChange = (itemId: string, selected: boolean) => {
    onItemSelectionChange(itemId, selected);
  };

  // Handle group selection using batch handler
  const handleGroupSelectionChange = (itemIds: string[], selected: boolean) => {
    onBatchSelectionChange(itemIds, selected);
  };

  return (
    <>
      <div className="mb-6">
        <Typo variant="h2" className="mb-4 text-gray-900">
          Chọn dịch vụ
        </Typo>
        <Alert variant="warning" className="gap-2">
          <AlertCircle className="h-3 w-3" />
          <Typo variant="muted" className="text-sm">
            Chọn các dịch vụ bạn muốn đặt. Lưu ý: mỗi đơn hàng chỉ được chọn các dịch vụ cùng địa điểm và cùng ngày.
          </Typo>
        </Alert>
      </div>

      {/* Service Groups */}
      <div className="space-y-6">
        {groupedCartItems.map((group, index) => (
          <CartServiceGroup
            key={index}
            siteName={group.siteName}
            date={group.date}
            items={group.items}
            selectedItems={selectedItems}
            onItemSelectionChange={handleItemSelectionChange}
            onGroupSelectionChange={handleGroupSelectionChange}
            onItemRemove={onItemRemove}
            onUpdateQuantity={onUpdateQuantity}
            disabled={isLoading || isFetching}
          />
        ))}
      </div>

      {/* Mobile Continue Button */}
      {/*<div className="mt-8 lg:hidden">
        <Button onClick={onContinueToCheckout} disabled={selectedItems.size === 0 || isLoading} className="w-full" size="lg">
          {isLoading ? 'Đang xử lý...' : `Tiếp tục (${selectedItems.size} dịch vụ)`}
        </Button>
      </div>*/}
    </>
  );
}
